<!--@format-->
<template>
    <div class="journey-email-step-widget">
        <div class="widget-contents-container">
            <div class="widget-header blue-gradient-bg">
                <h3>Email</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="input-fields">
                        <div class="step-name-container">
                            <label>Step Name</label>
                            <app-input v-model="step.name" />
                        </div>
                        <div class="subject-container">
                            <div class="subject-input-container">
                                <label>Subject</label>
                                <div class="subject-inserts">
                                    <emoji-dropdown
                                        :position="'right'"
                                        @input="appendSubject($event)"
                                    />
                                    <personalization-dropdown @input="appendSubject($event)" />
                                </div>
                            </div>
                            <app-input v-model="step.subject" />
                        </div>
                        <div class="from-container">
                            <label>From</label>
                            <app-input
                                v-model="step.fromName"
                                class="from-input name"
                                :label="'Name'"
                            />
                            <app-input
                                v-model="step.fromEmail"
                                class="from-input"
                                :label="'Email'"
                                :isValid="$v.step.fromEmail.email"
                                :error="`Please enter a valid email.`"
                            />
                            <domain-authentication-check
                                v-if="clientId"
                                :clientId="clientId"
                                :fromEmail="step.fromEmail"
                            />
                        </div>
                    </div>

                    <div
                        v-show="!isTemplateSelected"
                        class="strategy-type-container"
                        :class="{ selected: createFromScratch !== null }"
                    >
                        <div
                            class="strategy"
                            :class="{ selected: !createFromScratch }"
                            @click="selectExistingTemplate"
                        >
                            <div class="label-container">
                                <e16-from-template-svg /> <span>Choose Template</span>
                            </div>
                        </div>
                        <div
                            :class="{ selected: createFromScratch }"
                            class="strategy"
                            @click="selectFromScratch"
                        >
                            <div class="label-container">
                                <d16-from-scratch-svg /> <span>From Scratch</span>
                            </div>
                        </div>
                    </div>

                    <div v-show="!isTemplateSelected" class="template-selection-container">
                        <div class="template-type-selection-container">
                            <div class="template-type" @click="selectNonGraphicalEmail">
                                <div class="label-container">
                                    <u8-insert-email-template-svg />
                                    <span>Non-Graphical Email</span>
                                </div>

                                <div class="hover-container">
                                    <div v-if="createFromScratch" class="non-graphical">
                                        <img
                                            src="@/assets/images/journey-email-scratch-non-graphical.png"
                                        />
                                        <div class="title">Non-Graphical Email</div>
                                        <div class="description">
                                            Skip the graphics, and go straight to the point!
                                        </div>
                                    </div>

                                    <div v-else class="non-graphical">
                                        <img
                                            src="@/assets/images/journey-email-template-non-graphical.png"
                                        />
                                        <div class="title">Non-Graphical Email Templates</div>
                                        <div class="description">
                                            Choose from our wide variety of templates as a starting
                                            point, or create your own.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="template-type" @click="selectGraphicalEmail">
                                <div class="label-container">
                                    <t8-insert-graphical-email-template-svg />
                                    <span>Graphical Email</span>
                                </div>

                                <div class="hover-container">
                                    <div v-if="createFromScratch" class="scratch-hover graphical">
                                        <img
                                            src="@/assets/images/journey-email-scratch-graphical.png"
                                        />
                                        <div class="title">Create a Graphical Email</div>
                                        <div class="description">
                                            Start a graphical email from scratch with our
                                            easy-to-use drag and drop builder.
                                        </div>
                                    </div>
                                    <div v-else class="template-hover graphical">
                                        <img
                                            src="@/assets/images/journey-email-template-graphical.png"
                                        />
                                        <div class="title">Graphical Email Templates</div>
                                        <div class="description">
                                            Choose from our Graphical Email Templates, or your own
                                            created ones as a starting base.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-show="isTemplateSelected" class="content-container">
                        <label>Content</label>
                        <div class="template-container">
                            <div class="template">
                                <div class="preview">
                                    <img
                                        :src="
                                            step.previewImageUrl | templatePreviewImagePlaceholder
                                        "
                                    />
                                </div>
                            </div>
                            <div class="actions">
                                <app-button class="edit-button" @click="editTemplate"
                                    ><r7-edit-svg
                                /></app-button>
                                <app-button class="delete-button" @click="removeTemplate"
                                    ><s4-delete-svg
                                /></app-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isLoading" :loading="isLoading" @click="save"
                        >Save</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const PersonalizationDropdown = () => import('@/components/common/personalization-dropdown');
const R7EditSvg = () => import('@/components/svg/grid/r7-edit-svg');
const S4DeleteSvg = () => import('@/components/svg/grid/s4-delete-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const E16FromTemplateSvg = () => import('@/components/svg/grid/e16-from-template-svg');
const T8InsertGraphicalEmailTemplateSvg = () =>
    import('@/components/svg/grid/t8-insert-graphical-email-template-svg');
const U8InsertEmailTemplateSvg = () => import('@/components/svg/grid/u8-insert-email-template-svg');
const D16FromScratchSvg = () => import('@/components/svg/grid/d16-from-scratch-svg');
const DomainAuthenticationCheck = () => import('@/components/email/domain-authentication-check');
const EmojiDropdown = () => import('@/components/common/emoji-dropdown');
import { required, email } from 'vuelidate/lib/validators';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'journey-email-step-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'personalization-dropdown': PersonalizationDropdown,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'r7-edit-svg': R7EditSvg,
        's4-delete-svg': S4DeleteSvg,
        'e16-from-template-svg': E16FromTemplateSvg,
        't8-insert-graphical-email-template-svg': T8InsertGraphicalEmailTemplateSvg,
        'u8-insert-email-template-svg': U8InsertEmailTemplateSvg,
        'd16-from-scratch-svg': D16FromScratchSvg,
        'domain-authentication-check': DomainAuthenticationCheck,
        'emoji-dropdown': EmojiDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            createFromScratch: false,
            clientId: this.widget.options.clientId,
            agencyId: this.widget.options.agencyId,
            client: {
                name: null,
                logoImageUrl: null,
                footer: null,
            },
            step: {
                id: this.widget.options.stepId,
                journeyId: null,
                name: null,
                fromName: null,
                fromEmail: null,
                subject: null,
                emailBody: null,
                emailConfig: null,
                previewImageUrl: null,
            },
        };
    },

    validations() {
        return {
            step: {
                fromEmail: {
                    email,
                },
            },
        };
    },

    computed: {
        isTemplateSelected() {
            return this.step.emailBody || this.step.emailConfig;
        },
    },

    created() {
        if (this.clientId) {
            this.getClient();
        }

        if (this.step.id) {
            this.getStep();
        } else {
            this.isLoading = false;
        }

        EventBus.$on('email-template:select', this.attachTextTemplate);
        EventBus.$on('journey-step:update', this.updateStepContent);
        EventBus.$on('template:select', this.attachGraphicTemplate);
    },

    beforeDestroy() {
        EventBus.$off('email-template:select', this.attachTextTemplate);
        EventBus.$off('journey-step:update', this.updateStepContent);
        EventBus.$off('template:select', this.attachGraphicTemplate);
    },

    methods: {
        save() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            if (this.step.id) {
                this.patchStep()
                    .then(() => {
                        this.$helpers.showSuccessNotification('Step successfully saved.');
                        EventBus.$emit(`journey-step:update`, this.step);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your step.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                this.postStep();
            }
        },

        patchStep() {
            this.isLoading = true;

            var changes = [
                {
                    name: 'Name',
                    value: this.step.name,
                },
                {
                    name: 'FromName',
                    value: this.step.fromName,
                },
                {
                    name: 'FromEmail',
                    value: this.step.fromEmail,
                },
                {
                    name: 'Subject',
                    value: this.step.subject,
                },
                {
                    name: 'EmailBody',
                    value: this.step.emailBody,
                },
                {
                    name: 'EmailConfig',
                    value: this.step.emailConfig,
                },
                {
                    name: 'PreviewImageUrl',
                    value: this.step.previewImageUrl,
                },
            ];

            return this.$api.patch(`/api/JourneySteps/${this.step.id}`, changes);
        },

        postStep() {
            console.error('Not implemented');
        },

        getStep() {
            var filter = `$filter=Id eq ${this.step.id}`;
            var select = `$select=JourneyId,JourneyTemplateId,Name,FromEmail,FromName,Subject,EmailBody,EmailConfig,PreviewImageUrl`;

            this.$api
                .get(`/service/JourneySteps?${filter}&${select}`)
                .then((response) => {
                    var responseValue = response.data.value[0];

                    this.step.journeyId = responseValue.JourneyId;
                    this.step.journeyTemplateId = responseValue.JourneyTemplateId;
                    this.step.name = responseValue.Name;
                    this.step.fromEmail = responseValue.FromEmail;
                    this.step.fromName = responseValue.FromName;
                    this.step.subject = responseValue.Subject;
                    this.step.emailBody = responseValue.EmailBody;
                    this.step.emailConfig = responseValue.EmailConfig;
                    this.step.previewImageUrl = responseValue.PreviewImageUrl;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your step.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        selectNonGraphicalEmail() {
            if (this.createFromScratch) {
                EventBus.$emit('open-widget', {
                    name: 'journey-email-step-content-widget',
                    options: {
                        journeyStepId: this.step.id,
                        clientId: this.clientId,
                        agencyId: this.agencyId,
                        startFromScratch: true,
                    },
                });
            } else {
                EventBus.$emit('open-widget', {
                    name: 'plain-text-email-templates-widget',
                    editBeforeAdding: true,
                    clientId: this.clientId,
                    agencyId: this.agencyId,
                });
            }
        },

        selectGraphicalEmail() {
            if (this.createFromScratch) {
                this.removeTemplate();

                this.patchStep().finally(() => {
                    this.goToEmailDesign();
                    this.closeWidget();
                });
            } else {
                EventBus.$emit('open-widget', {
                    name: 'graphical-email-templates-widget',
                    clientId: this.clientId,
                    agencyId: this.agencyId,
                });
            }
        },

        goToEmailDesign(templateId) {
            var queryParam = templateId ? `templateId=${templateId}` : ``;

            if (this.step.journeyTemplateId) {
                this.$router.push(
                    `/JourneyTemplates/${this.step.journeyTemplateId}/JourneySteps/${this.step.id}/EmailDesign?${queryParam}`,
                );
            } else {
                this.$router.push(
                    `/Journeys/${this.step.journeyId}/JourneySteps/${this.step.id}/EmailDesign?${queryParam}`,
                );
            }
        },

        selectFromScratch() {
            this.createFromScratch = true;
        },

        selectExistingTemplate() {
            this.createFromScratch = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        attachTextTemplate(template) {
            this.step.subject = template.subject || this.step.subject;
            this.step.emailConfig = null;
            this.step.emailBody = template.body;
            this.step.previewImageUrl = template.previewImageUrl;

            this.patchStep().finally(() => {
                this.isLoading = false;
            });
        },

        updateStepContent(updatedStep) {
            this.step.emailConfig = updatedStep.emailConfig;
            this.step.emailBody = updatedStep.emailBody;
            this.step.previewImageUrl = updatedStep.previewImageUrl;
        },

        attachGraphicTemplate(template) {
            this.step.emailBody = null;
            this.step.previewImageUrl = template.previewImageUrl;

            var emailConfig = JSON.parse(template.configuration);

            var isShared = template.clientId !== this.clientId;
            if (isShared) {
                emailConfig.logo.image.url = this.client.templateLogoImageUrl;
                emailConfig.logo.text.value = `<h1>${this.client.name}</h1>`;
                emailConfig.footer.value = this.client.footer;
                this.calculateLogoDimensions(emailConfig);
            }

            this.step.emailConfig = JSON.stringify(emailConfig);

            this.patchStep()
                .then(() => {
                    EventBus.$emit(`journey-step:update`, this.step);
                    this.goToEmailDesign(template.id);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your journey',
                        { error },
                    );
                });
        },

        calculateLogoDimensions(emailConfig) {
            var image = new Image();
            image.onload = function () {
                emailConfig.logo.image.width = this.width;
                emailConfig.logo.image.height = this.height;
            };
            image.src = emailConfig.logo.image.url;
        },

        editTemplate() {
            if (this.step.emailBody) {
                EventBus.$emit('open-widget', {
                    name: 'journey-email-step-content-widget',
                    options: {
                        journeyStepId: this.step.id,
                        clientId: this.clientId,
                    },
                });
            } else {
                this.patchStep().finally(() => {
                    this.goToEmailDesign();
                    this.closeWidget();
                });
            }
        },

        removeTemplate() {
            this.step.emailBody = null;
            this.step.emailConfig = null;
            this.step.previewImageUrl = null;
        },

        appendSubject($event) {
            if (this.step.subject) {
                this.step.subject += `${$event}`;
            } else {
                this.step.subject = $event;
            }
        },

        getClient() {
            this.$api
                .get(
                    `/service/Clients?$filter=Id eq ${this.clientId}&$select=Id,Name,TemplateLogoImageUrl,TemplateFooter`,
                )
                .then((response) => {
                    this.client.name = response.data.value[0].Name;
                    this.client.logoImageUrl = response.data.value[0].TemplateLogoImageUrl;
                    this.client.footer = response.data.value[0].TemplateFooter;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your company info',
                        { error },
                    );
                });
        },
    },
};
</script>

<style>
.journey-email-step-widget .content-wrapper {
    padding: 0px !important;
}

.journey-email-step-widget .content-wrapper .input-fields {
    padding: 45px 35px 0px 35px;
}

.journey-email-step-widget .input-fields .personalization-dropdown {
    width: 250px;
}

.journey-email-step-widget .step-name-container,
.journey-email-step-widget .subject-container {
    margin-bottom: 22px;
}

.journey-email-step-widget .subject-container .subject-input-container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    z-index: 20;
    position: relative;
}

.journey-email-step-widget .subject-container .subject-inserts {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.journey-email-step-widget .subject-container .subject-inserts .emoji-dropdown {
    margin-right: 20px;
    z-index: 20;
    position: relative;
}

.journey-email-step-widget .subject-container .subject-input-container label {
    margin: auto 0;
}

.journey-email-step-widget .from-container .from-input.name {
    margin-bottom: 25px;
}

.journey-email-step-widget .subject-container .subject-input-container .app-multiselect {
    width: 210px;
}

.journey-email-step-widget .strategy-type-container {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
}

.journey-email-step-widget .strategy-type-container.selected {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    position: relative;
}

.journey-email-step-widget .strategy-type-container.selected::after {
    content: '';
    position: absolute;
    display: inline-block;
    width: calc(100% + 2px);
    height: 2px;
    background: #18aeff;
    bottom: 0px;
    z-index: 1;
}

.journey-email-step-widget .strategy-type-container .strategy {
    display: flex;
    justify-items: center;
    align-items: center;
    width: calc(50% - 25px);
    height: 74px;
    border: 2px solid #e9e9ef;
    border-radius: 11px;
    margin: 0px 35px;
}

.journey-email-step-widget .strategy-type-container.selected .strategy {
    border-bottom: 2px solid transparent;
    border-radius: 11px 11px 0px 0px;
}

.journey-email-step-widget .strategy-type-container .strategy.selected {
    border: 2px solid #18aeff;
    border-bottom: 2px solid #ffffff;
    border-radius: 11px 11px 0px 0px;
    position: relative;
    z-index: 2;
}

.journey-email-step-widget .template-selection-container {
    width: 100%;
}

.journey-email-step-widget .template-selection-container .template-type-selection-container {
    display: flex;
    justify-items: center;
    align-items: center;
}

.journey-email-step-widget
    .template-selection-container
    .template-type-selection-container
    .template-type {
    display: flex;
    justify-items: center;
    align-items: center;
    width: calc(50% - 25px);
    height: 74px;
    border: 2px solid #e9e9ef;
    border-radius: 11px;
    background-color: #fff;
    margin: 25px 35px;
}

.journey-email-step-widget
    .template-selection-container
    .template-type-selection-container
    .template-type
    span {
    margin: 0 10px;
}

.journey-email-step-widget
    .template-selection-container
    .template-type-selection-container
    .template-type:hover {
    background-color: #f7f7f8;
    cursor: pointer;
}

.journey-email-step-widget .strategy-type-container .strategy span {
    margin: 0 10px;
    font: Regular 22px/24px Nunito;
    letter-spacing: 0;
    color: #877d88;
}

.journey-email-step-widget .strategy-type-container .strategy.selected span {
    color: #18aeff;
}

.journey-email-step-widget .strategy-type-container .strategy:hover {
    background-color: #f7f7f8;
    cursor: pointer;
}

.journey-email-step-widget .content-container {
    padding: 35px;
}

.journey-email-step-widget .template-container {
    display: flex;
    align-items: stretch;
}

.journey-email-step-widget .template-container .template {
    width: 164px;
    height: 169px;
    background: #ffffff;
    box-shadow: 0px 3px 6px #8274e833;
    border: 2px solid #e9e9ef;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
}

.journey-email-step-widget .template-container .template .preview {
    overflow: hidden;
    border-radius: 18px;
}

.journey-email-step-widget .template-container .template .preview img {
    width: 100%;
}

.journey-email-step-widget .template-container .actions {
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.journey-email-step-widget .template-container .actions .app-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    padding: 0;
    margin: 10px 0;
}

.journey-email-step-widget .template-container .actions .app-button svg {
    width: 20px;
}

.journey-email-step-widget .template-container .actions .app-button svg path,
.journey-email-step-widget .template-container .actions .app-button svg rect {
    fill: #ffffff;
}

.journey-email-step-widget .template-container .actions .delete-button {
    background: #f30070;
}

.journey-email-step-widget .template-container .actions .delete-button:hover {
    background: #c3025b;
}

.journey-email-step-widget .label-container {
    display: flex;
    justify-items: center;
    align-items: center;
    margin: 0 auto;
}

.journey-email-step-widget .template-type .label-container svg {
    width: 46px;
    height: auto;
}

.journey-email-step-widget .strategy.selected .d16-from-scratch-svg .ruler,
.journey-email-step-widget .strategy.selected .d16-from-scratch-svg .pencil-b,
.journey-email-step-widget .strategy.selected .d16-from-scratch-svg .pencil-a,
.journey-email-step-widget .strategy.selected .d16-from-scratch-svg .bottom-right-b {
    fill: #18aeff;
}

.journey-email-step-widget .strategy.selected .d16-from-scratch-svg .box rect,
.journey-email-step-widget .strategy.selected .e16-from-template-svg .corner-box rect,
.journey-email-step-widget .strategy.selected .e16-from-template-svg .writing-lines,
.journey-email-step-widget .strategy.selected .e16-from-template-svg .img-d,
.journey-email-step-widget .strategy.selected .e16-from-template-svg .img-e {
    stroke: #18aeff;
}

.journey-email-step-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.journey-email-step-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.journey-email-step-widget .hover-container {
    box-shadow: 0px 3px 6px #8274e833;
    border: 2px solid #e9e9ef;
}

.journey-email-step-widget .template-type {
    z-index: 5;
}

.journey-email-step-widget .template-type .hover-container .non-graphical,
.journey-email-step-widget .template-type .hover-container .graphical {
    display: none;
}

.journey-email-step-widget .template-type:hover .hover-container .non-graphical {
    position: absolute;
    display: flex;
    flex-direction: column;
    z-index: 10;
    left: -335px;
    margin-top: -300px;
    background-color: #fff;
    width: 398px;
    box-shadow: 0px 3px 6px #8274e833;
}

.journey-email-step-widget .template-type:hover .hover-container .graphical {
    position: absolute;
    display: flex;
    flex-direction: column;
    z-index: 10;
    left: 37px;
    margin-top: -300px;
    background-color: #fff;
    width: 398px;
    box-shadow: 0px 3px 6px #8274e833;
}

.journey-email-step-widget .hover-container .non-graphical .title,
.journey-email-step-widget .hover-container .graphical .title {
    font-weight: 600;
    padding: 16px 25px 9px 25px;
}

.journey-email-step-widget .hover-container .non-graphical .description,
.journey-email-step-widget .hover-container .graphical .description {
    margin-bottom: 25px;
    padding: 0px 25px;
    line-height: 2;
}

.journey-email-step-widget .template-type .hover-container .template-hover.non-graphical {
    display: none;
}

.journey-email-step-widget .template-type:hover .hover-container .template-hover.non-graphical {
    position: absolute;
    display: flex;
    flex-direction: column;
    z-index: 100;
    right: 635px;
    top: 350px;
    background-color: #fff;
    width: 398px;
}

.journey-email-step-widget .hover-container .template-hover.non-graphical .title {
    font-weight: 600;
    padding: 10px 25px;
}

.journey-email-step-widget .hover-container .template-hover.non-graphical .description {
    margin-bottom: 50px;
    padding: 0px 25px;
}

.journey-email-step-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
